
import { useState, useMemo, useCallback, useEffect } from "react";
import classes from "./timeline.module.css";
import { toast } from "react-toastify";
import axios from "../../../Axios";
import { Urls } from "../../../Urls";
import ReactTooltip from "react-tooltip";
// import "react-widgets/dist/css/react-widgets.css";
// import { DateTimePicker } from "react-widgets";
import Moment from "moment";
import momentLocalizer from "react-widgets-moment";
import { DateFormatter4 } from "../../../Constants";
import ControlledPopupModal from "../../Shared/PopupModal/ControlledPopupModal/ControlledPopupModal";
import { useIndentTracking } from "../hooks/useIndentTracking";
import StatusRemarkPopup from "./StatusRemarkPopup";

Moment.locale("en");
momentLocalizer();

// Constants
const STATUS_TYPES = {
  YET_TO_BE_PLACED: "yet to be placed",
  PLACED: "placed",
  DISPATCHED: "dispatched",
  REACHED: "reached",
  UNLOADED: "unloaded",
  PAYMENT_APPROVED: "paymentapproved",
  PAID: "paid",
};

const STATUS_ORDER = [
  STATUS_TYPES.YET_TO_BE_PLACED,
  STATUS_TYPES.PLACED,
  STATUS_TYPES.DISPATCHED,
  STATUS_TYPES.REACHED,
  STATUS_TYPES.UNLOADED,
  STATUS_TYPES.PAYMENT_APPROVED,
  STATUS_TYPES.PAID,
];

const STATUS_TRANSITIONS = {
  [STATUS_TYPES.PLACED]: [STATUS_TYPES.YET_TO_BE_PLACED],
  [STATUS_TYPES.DISPATCHED]: [STATUS_TYPES.PLACED],
  [STATUS_TYPES.REACHED]: [STATUS_TYPES.DISPATCHED],
  [STATUS_TYPES.UNLOADED]: [STATUS_TYPES.REACHED],
  [STATUS_TYPES.PAYMENT_APPROVED]: [STATUS_TYPES.UNLOADED],
  [STATUS_TYPES.PAID]: [STATUS_TYPES.PAYMENT_APPROVED],
};

const COLORS = {
  COMPANY: "#393168",
  TRANSPORTER: "#54AF3A",
  INACTIVE: "#727271",
  PRIMARY: "#393186",
  SYSTEM: "#d9534f",
};

// Utility Functions
const formatDateToISO = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");

  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatDisplayDate = (inputdate) => {
  const date = new Date(inputdate);
  const pad = (n) => String(n).padStart(2, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const dt = pad(date.getDate());
  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return (
    <>
      <span>{dt + "-" + month + "-" + year}</span>
      <br />
      <span>{time}</span>
    </>
  );
};

const getPreviousStatus = (currentStatus) => {
  const idx = STATUS_ORDER.indexOf(currentStatus);
  return idx > 0 ? STATUS_ORDER[idx - 1] : null;
};

const formatHistoryTimestamp = (inputdate) => {
  const date = new Date(inputdate);
  if (isNaN(date)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day}-${month}-${year}, ${time}`;
};

const whoUpdated = (entry) => {
  if (entry.updated_by_name) {
    return entry.updated_by_email
      ? `${entry.updated_by_name} (${entry.updated_by_email})`
      : entry.updated_by_name;
  }
  return entry.updated_by === "company" || entry.updated_by === "client"
    ? "Me"
    : entry.updated_by === "system"
      ? "System"
      : "Transporter";
};

// Backend sends `history` as the full log of prior edits, already ordered oldest -> newest,
// ending right before the current (top-level) value. Preserve that order, just append current last.
const getStatusHistoryEntries = (statusData) => {
  if (!statusData?.hasHistory) return [];
  const current = statusData.values[0];
  return [...current.history, current];
};

// Status Processing Hook
const useProcessedStatuses = (status_track) => {
  return useMemo(() => {
    if (!status_track || status_track.length === 0) {
      return {
        placed: { done_by: "", values: [] },
        dispatched: { done_by: "", values: [] },
        reached: { done_by: "", values: [] },
        unloaded: { done_by: "", values: [] },
        paymentapproved: { done_by: "", values: [] },
        paid: { done_by: "", values: [] },
      };
    }

    const processStatus = (statusType) => {
      let done_by = "";
      const values = [];
      let completed = false;

      status_track.forEach((status) => {
        if (status.value === statusType) {
          const updated_by = !status.updated_by
            ? "company"
            : status.updated_by === "client"
              ? "company"
              : status.updated_by;

          if (status.history && status.history.length > 0 && !completed) {
            values.push({
              history: status.history,
              value: statusType,
              timestamp: status.timestamp,
              comments: status.comments,
              updated_by,
              updated_by_name: status.updated_by_name,
              updated_by_email: status.updated_by_email,
            });
            completed = true;
          } else if (!completed) {
            values.push({
              value: statusType,
              timestamp: status.timestamp,
              comments: status.comments,
              updated_by,
              updated_by_name: status.updated_by_name,
              updated_by_email: status.updated_by_email,
            });
          }

          if (updated_by === "system") {
            done_by = "system";
          } else if (
            updated_by === "transporter" &&
            done_by !== "company" &&
            done_by !== "system"
          ) {
            done_by = "transporter";
          } else if (done_by === "") {
            done_by = "company";
          }
        }
      });

      const current = values[0];
      const priorEntries = current?.history || [];
      const hasHistory = priorEntries.some(
        (h) =>
          h.timestamp !== current.timestamp || h.comments !== current.comments
      );

      return { done_by, values, hasHistory };
    };



    return {
      placed: processStatus(STATUS_TYPES.PLACED),
      dispatched: processStatus(STATUS_TYPES.DISPATCHED),
      reached: processStatus(STATUS_TYPES.REACHED),
      unloaded: processStatus(STATUS_TYPES.UNLOADED),
      paymentapproved: processStatus(STATUS_TYPES.PAYMENT_APPROVED),
      paid: processStatus(STATUS_TYPES.PAID),
    };
  }, [status_track]);
};

const Timeline = ({
  status_track,
  indent_id,
  transporter_id,
  indent_id_sub,
  setShow,
  index,
  getData,
  isAccountant,
  checkPresentInvoice,
  indent,
  isRestricted,
}) => {
  const [selectedvalue, setselectedvalue] = useState("");
  const [stateselected, setStateselected] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selecteddate, setSelectedDate] = useState(new Date());
  const [comments, setComments] = useState({
    placed: "",
    dispatched: "",
    reached: "",
    unloaded: "",
    paymentapproved: "",
    paid: "",
  });
  const [maxdate, setmaxdate] = useState(new Date());
  const [mindate, setMindate] = useState(null);

  const { trackIndentEvent } = useIndentTracking();
  const processedStatuses = useProcessedStatuses(status_track);
  const isIndentTrackingPage = window.location.pathname === "/indenttracking";
  const hasAnyHistory = Object.values(processedStatuses).some(
    (s) => s?.hasHistory
  );

  // Calculate min/max dates for date picker - IMPROVED LOGIC from TimelineV2
  const calculateDateBounds = useCallback(
    (statusKey) => {
      const statusIdx = STATUS_ORDER.indexOf(statusKey);
      const prevKey = statusIdx > 0 ? STATUS_ORDER[statusIdx - 1] : null;

      let min = null;
      let max = new Date(); // current time

      // If there is a previous status, use its timestamp as min
      if (prevKey) {
        // 1) If previous status is explicitly recorded in processed data
        const prevValues = processedStatuses[prevKey]?.values;
        const prevDoneBy = processedStatuses[prevKey]?.done_by;

        if (prevValues?.length) {
          const match =
            prevValues.find((v) => v.updated_by === prevDoneBy) ||
            prevValues[0];

          if (match?.timestamp) {
            min = new Date(match.timestamp);
          }
        }
        // 2) Fallback: if previous is "yet to be placed", use it from raw status_track
        else if (prevKey === STATUS_TYPES.YET_TO_BE_PLACED && status_track) {
          const ytbp = status_track.find(
            (s) => s.value === STATUS_TYPES.YET_TO_BE_PLACED
          );
          if (ytbp?.timestamp) {
            min = new Date(ytbp.timestamp);
          }
        }
      }

      // Final fallback: if still no min and we have a "yet to be placed" in status_track
      if (!min && status_track?.length) {
        const ytbp = status_track.find(
          (s) => s.value === STATUS_TYPES.YET_TO_BE_PLACED
        );
        if (ytbp?.timestamp) {
          min = new Date(ytbp.timestamp);
        }
      }

      // Set max date from next completed status
      for (let i = statusIdx + 1; i < STATUS_ORDER.length; i++) {
        const nextKey = STATUS_ORDER[i];
        const nextValues = processedStatuses[nextKey]?.values;
        const nextDoneBy = processedStatuses[nextKey]?.done_by;

        if (nextValues?.length) {
          const match =
            nextValues.find((v) => v.updated_by === nextDoneBy) ||
            nextValues[0];

          if (match?.timestamp) {
            max = new Date(match.timestamp);
            break;
          }
        }
      }

      return { min, max };
    },
    [status_track, processedStatuses]
  );

  const clickhandler = useCallback(
    (val, shouldupdate) => {
      trackIndentEvent(
        "indent tracking",
        "user has clicked on timeline status update",
        `indent id :${indent_id}, indent sub id :${indent_id_sub}, status :${val}`
      );

      if (isRestricted) {
        toast.info("You don't have permission to perform this action.");
        return;
      }

      if (indent?.is_cancelled?.status) {
        toast.error("Cannot update status for a cancelled indent.");
        return;
      }

      if (val === STATUS_TYPES.PAYMENT_APPROVED) {
        const invoiceStatus = checkPresentInvoice(indent, "invoice");
        if (invoiceStatus === "not_uploaded_invoice") {
          toast.error("Cannot approve payment without invoice.");
          return;
        }
      }

      if (isAccountant) {
        if (val !== "paid") {
          toast.error(`Only Operations team can update the status to ${val}`);
          return;
        }
      } else {
        if (val === "paid") {
          toast.error("Only Accounts team can update status to Paid");
          return;
        }
      }

      if (val === selectedvalue) {
        setStateselected(false);
        setselectedvalue("");
        return;
      }

      setStateselected(true);
      setselectedvalue(val);

      const { min, max } = calculateDateBounds(val);

      setMindate(min);
      setmaxdate(max);

      // Set initial selected date to existing value or current time
      const currentStatus = processedStatuses[val];
      if (currentStatus?.values.length > 0) {
        const relevantStatus = currentStatus.values.find(
          (v) => v.updated_by === currentStatus.done_by
        );
        if (relevantStatus) {
          setSelectedDate(new Date(relevantStatus.timestamp));
          return;
        }
      }

      setSelectedDate(new Date());
    },
    [
      selectedvalue,
      trackIndentEvent,
      indent_id,
      indent_id_sub,
      calculateDateBounds,
      processedStatuses,
    ]
  );

  const updatestatus = useCallback(() => {
    const currentStatus = status_track?.[status_track?.length - 1]?.value;
    const isEditingExisting = processedStatuses[selectedvalue]?.values.length > 0;

    if (
      !isEditingExisting &&
      !STATUS_TRANSITIONS?.[selectedvalue]?.includes(currentStatus)
    ) {
      toast.error(
        `Please update your ${STATUS_TRANSITIONS?.[selectedvalue]?.[0]} status first`
      );
      return;
    }

    // VALIDATION 2: Check if date is selected
    if (!selecteddate) {
      toast.error("Please select a valid date and time");
      return;
    }

    // VALIDATION 3: Check min date constraint
    if (mindate && selecteddate <= mindate) {
      toast.error("Selected time must be after the previous status time");
      return;
    }

    // VALIDATION 4: Check max date constraint (cannot be in future)
    if (maxdate && selecteddate) {
      const selected = new Date(selecteddate);
      const max = new Date(maxdate);

      selected.setSeconds(0, 0);
      max.setSeconds(0, 0);

      if (selected > max) {
        toast.error("Selected time cannot be in the future");
        return;
      }
    }


    const seltimestamp = formatDateToISO(new Date(selecteddate));
    const payload = {
      value: selectedvalue,
      timestamp: `${seltimestamp.split("T")[0]} ${seltimestamp.split("T")[1]
        }:00.000000+05:30`,
      updated_by: "company",
      comments: comments[selectedvalue] || "",
    };

    trackIndentEvent(
      "indent tracking",
      "user has successfully updated timeline status",
      `indent id :${indent_id}, indent sub id :${indent_id_sub}, status :${selectedvalue}`
    );

    setShow(true);
    axios
      .put(
        `${Urls.indent}?transporter_id=${transporter_id}&indent_id=${indent_id}&field=status_track_update&indent_id_sub=${indent_id_sub}`,
        payload
      )
      .then((response) => {
        if (response.data.status === false) {
          toast.error(response.data.error);
        }
        else {
          toast.success("Status updated successfully");
          getData();
          setShow(false);
          setStateselected(false);
          setselectedvalue("");
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to update status. Please try again.");
        setShow(false);
      });
  }, [
    status_track,
    selectedvalue,
    selecteddate,
    comments,
    mindate,
    maxdate,
    processedStatuses,
    trackIndentEvent,
    indent_id,
    indent_id_sub,
    transporter_id,
    setShow,
    getData,
  ]);

  const handleCommentChange = useCallback(
    (value) => {
      setComments((prev) => ({ ...prev, [selectedvalue]: value }));
    },
    [selectedvalue]
  );

  // Timeline Item Component
  const TimelineItem = ({
    status,
    label,
    onClick,
    showLine = true,
    circleClass = "circle",
  }) => {
    const statusData = processedStatuses[status];
    const color = statusData.done_by
      ? statusData.done_by === "transporter"
        ? COLORS.TRANSPORTER
        : statusData.done_by === "system"
          ? COLORS.SYSTEM
          : COLORS.COMPANY
      : COLORS.INACTIVE;

    return (
      <div className={classes["timeline-item"]}
        onClick={onClick}
        style={{
          cursor:
            isRestricted || indent?.is_cancelled?.status
              ? "not-allowed"
              : "pointer",
        }}>
        <div className={classes["timeline-item-content"]}>
          <span className={classes["tag"]} style={{ color }}>
            {label}
          </span>
          <br />
          <time className={classes["tag"]} style={{ color, fontSize: "10px" }}>
            {statusData.values.map((s, i) => {
              if (s.updated_by === statusData.done_by) {
                return (
                  <i key={`${status}${i}`}>{formatDisplayDate(s.timestamp)}</i>
                );
              }
              return null;
            })}
          </time>
          {statusData.values.length > 0 && (
            <>
              <span data-tip data-for={`${status}${index}`}>
                <i className="fa fa-info" style={{ padding: "0 5%", color }} />
              </span>
              <ReactTooltip
                id={`${status}${index}`}
                className={classes["tooltip"]}
                place="bottom"
                effect="float"
                backgroundColor="#393186 !important"
              >
                <span>History</span>
                {(statusData.values[0].history &&
                  statusData.values[0].history.length > 0
                  ? statusData.values[0].history
                  : statusData.values
                ).map((s, i) => (
                  <div className="row" key={`history${status}${i}`}>
                    <div className="col-md-3">
                      {s.updated_by === "company"
                        ? "Me"
                        : s.updated_by === "system"
                          ? "System"
                          : "Transporter"}
                      :
                    </div>
                    <div className="col-md-9">
                      {s.timestamp ? DateFormatter4(s.timestamp) : ""}{" "}
                      {s.comments ? `(${s.comments})` : ""}
                    </div>
                  </div>
                ))}
              </ReactTooltip>
            </>
          )}
          <span
            className={classes[circleClass]}
            style={{ backgroundColor: color }}
          />
          {showLine && <span className={classes["line"]} />}
        </div>
      </div>
    );
  };

  return (
    <div id={`timeline${index}`} style={{ position: "relative" }}>
      {hasAnyHistory && (
        <span
          className={classes["history-icon-button"]}
          onClick={() => setShowHistoryModal(true)}
          data-tip
          data-for={`history-icon-tip${index}`}
        >
          <i className="fa fa-history" />
        </span>
      )}
      <ReactTooltip
        id={`history-icon-tip${index}`}
        place="top"
        effect="solid"
      >
        View Edit History
      </ReactTooltip>
      <div className={classes["timeline-container"]}>
        {/* Yet to be Placed */}
        <div className={classes["timeline-item"]}>
          <div className={classes["timeline-item-content"]}>
            <span className={classes["tag"]} style={{ color: COLORS.PRIMARY }}>
              Yet to be placed
            </span>
            <br />
            <time
              className={classes["tag"]}
              style={{ color: COLORS.PRIMARY, fontSize: "10px" }}
            >
              {status_track?.map((st) => {
                if (st.value === STATUS_TYPES.YET_TO_BE_PLACED) {
                  return (
                    <i key={st.value}>{formatDisplayDate(st.timestamp)}</i>
                  );
                }
                return null;
              })}
            </time>
            <span
              className={classes["circle"]}
              style={{ backgroundColor: COLORS.PRIMARY }}
            />
            <span className={classes["line"]} />
          </div>
        </div>

        {/* Placed */}
        {isIndentTrackingPage ? (
          <TimelineItem
            status="placed"
            label="Placed"
            onClick={() =>
              clickhandler(
                STATUS_TYPES.PLACED,
                processedStatuses.placed.done_by !== "company"
              )
            }
          />
        ) : (
          <TimelineItem status="placed" label="Placed" />
        )}

        {/* Dispatched */}
        {isIndentTrackingPage ? (
          <TimelineItem
            status="dispatched"
            label="Dispatched"
            onClick={() =>
              clickhandler(
                STATUS_TYPES.DISPATCHED,
                processedStatuses.dispatched.done_by !== "company"
              )
            }
          />
        ) : (
          <TimelineItem status="dispatched" label="Dispatched" />
        )}

        {/* Reached */}
        {isIndentTrackingPage ? (
          <TimelineItem
            status="reached"
            label="Reached"
            onClick={() =>
              clickhandler(
                STATUS_TYPES.REACHED,
                processedStatuses.reached.done_by !== "company"
              )
            }
          />
        ) : (
          <TimelineItem status="reached" label="Reached" />
        )}

        {/* Unloaded */}
        {isIndentTrackingPage ? (
          <TimelineItem
            status="unloaded"
            label="Unloaded"
            onClick={() =>
              clickhandler(
                STATUS_TYPES.UNLOADED,
                processedStatuses.unloaded.done_by !== "company"
              )
            }
          />
        ) : (
          <TimelineItem status="unloaded" label="Unloaded" />
        )}

        {/* Payment Approved */}
        {isIndentTrackingPage ? (
          <TimelineItem
            status="paymentapproved"
            label="Payment Approved"
            circleClass="circle1"
            onClick={() =>
              clickhandler(
                STATUS_TYPES.PAYMENT_APPROVED,
                processedStatuses.paymentapproved.done_by !== "company"
              )
            }
          />
        ) : (
          <TimelineItem
            status="paymentapproved"
            label="Payment Approved"
            circleClass="circle1"
          />
        )}

        {/* Paid */}
        {isIndentTrackingPage ? (
          <TimelineItem
            status="paid"
            label="Paid"
            circleClass="circle1"
            showLine={false}
            onClick={() =>
              clickhandler(
                STATUS_TYPES.PAID,
                processedStatuses.paid.done_by !== "company"
              )
            }
          />
        ) : (
          <TimelineItem
            status="paid"
            label="Paid"
            circleClass="circle1"
            showLine={false}
          />
        )}
      </div>

      <ControlledPopupModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title="Status Edit History"
        size="md"
        backdrop={true}
        content={
          <div className={classes["history-log"]}>
            {STATUS_ORDER.filter(
              (status) => status !== STATUS_TYPES.YET_TO_BE_PLACED
            ).map((status) => {
              const statusData = processedStatuses[status];
              if (!statusData?.hasHistory) return null;
              const entries = getStatusHistoryEntries(statusData);

              return (
                <div
                  key={`history-block-${status}`}
                  className={classes["history-block"]}
                >
                  <div className={classes["history-status-label"]}>
                    {status.replace("paymentapproved", "Payment Approved")}
                  </div>
                  {entries.map((entry, i) => {
                    const isLatest = i === entries.length - 1;
                    return (
                      <div
                        key={`log-${status}${i}`}
                        className={classes["history-log-entry"]}
                      >
                        <span
                          className={
                            isLatest
                              ? classes["history-dot-latest"]
                              : classes["history-dot"]
                          }
                        />
                        <div className={classes["history-log-content"]}>
                          <div className={classes["history-log-time"]}>
                            {formatHistoryTimestamp(entry.timestamp)}
                            {isLatest && (
                              <span className={classes["history-tag-current"]}>
                                Current
                              </span>
                            )}
                          </div>
                          <div className={classes["history-log-who"]}>
                            {whoUpdated(entry)}
                          </div>
                          {entry.comments && (
                            <div className={classes["history-log-comment"]}>
                              "{entry.comments}"
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        }
      />

      {/* Modal */}
      <ControlledPopupModal
        isOpen={stateselected}
        onClose={() => setStateselected(false)}
        title={`Update Truck Status: ${selectedvalue.toUpperCase()}`}
        // showLogo={true}
        size="md"
        backdrop={true}
        content={
          <StatusRemarkPopup
            indent_id={indent_id}
            indent_id_sub={indent_id_sub}
            selecteddate={selecteddate}
            setSelectedDate={setSelectedDate}
            mindate={mindate}
            maxdate={maxdate}
            comments={comments}
            selectedvalue={selectedvalue}
            handleCommentChange={handleCommentChange}
            updatestatus={updatestatus}
            historyEntries={getStatusHistoryEntries(
              processedStatuses[selectedvalue]
            )}
          />
        }
      />
    </div>
  );
};

export default Timeline;
